/**
 * Черновик сообщения клиенту в карточке сделки (#1675; спека
 * docs/plans/2026-10-14-deal-client-message-draft.md) — кнопка рядом со
 * следующим действием.
 *
 * Три контракта одного нажатия:
 *
 * 1. `POST /api/v1/deals/:stateId/client-message` (ui-worker → edge-api) —
 *    тела нет: всё, что нужно, — голова сделки по `:stateId`.
 * 2. `dealClientMessageServiceRequestSchema` — edge-api → workflows-api:
 *    организация и пользователь из сессии, id состояния, которое edge уже
 *    проверил на видимость. Платный вызов живёт в workflows-api, как у черновика
 *    FAQ (#843): одна дверь для платного пути, расход пишет только он.
 * 3. `dealClientMessageModelOutputSchema` — разбор ответа модели;
 *    `dealClientMessageResponseSchema` — что видит клиент.
 *
 * Черновик НЕ хранится (спека §3 M7): нет таблицы и срока хранения, экран
 * держит его в памяти, повторное нажатие строит заново.
 */
import { z } from 'zod/v4';

/** Потолок текста черновика, в символах: короткое сообщение, не письмо-простыня. */
export const DEAL_CLIENT_MESSAGE_MAX_CHARS = 1_500;

/**
 * Черновиков на организацию за скользящие сутки (спека §3 M2). Платит
 * платформа, кошелёк клиента не спрашивается — потолок и есть граница
 * расходов кнопки, как у черновика FAQ.
 */
export const DEAL_CLIENT_MESSAGE_DAILY_LIMIT = 200;
/** Черновиков на пользователя за скользящие сутки — внутри потолка организации. */
export const DEAL_CLIENT_MESSAGE_USER_DAILY_LIMIT = 60;

/**
 * Сколько edge-api ждёт workflows-api. Вызов модели ограничен своим
 * таймаутом с запасом меньше этого: оплаченный вызов успевает записать
 * расход до того, как edge перестанет ждать. Клиент ждёт на 10 с дольше,
 * чтобы прочитать ответ edge «слишком долго».
 */
export const DEAL_CLIENT_MESSAGE_PROXY_DEADLINE_MS = 60_000;

/** edge-api → workflows-api. Организация и пользователь — из сессии, не от клиента. */
export const dealClientMessageServiceRequestSchema = z.object({
  orgId: z.string().min(1),
  /** Потолок пользователя. Нет — только потолок организации. */
  userId: z.string().min(1).max(200).optional(),
  stateId: z.guid(),
  traceId: z.string().optional(),
});
export type DealClientMessageServiceRequest = z.infer<
  typeof dealClientMessageServiceRequestSchema
>;

/**
 * Ответ модели — форма без границы длины: разбор (workflows-api) редактирует
 * ПДн и только потом режет до `DEAL_CLIENT_MESSAGE_MAX_CHARS`, чтобы
 * заполнитель вроде `[PHONE]` не вывел строку за контракт. Границу в самом
 * вызове держит строгая JSON-схема (`maxLength`).
 */
export const dealClientMessageModelOutputSchema = z.object({
  message: z.string(),
});

/** Сам черновик. `generated: true` — пометка «сгенерировано» на черновике. */
export const dealClientMessageDraftSchema = z.object({
  generated: z.literal(true),
  text: z.string().min(1).max(DEAL_CLIENT_MESSAGE_MAX_CHARS),
  /**
   * Язык черновика — язык анализа проекта сделки; `auto` — язык текстов
   * состояния, код тогда неизвестен.
   */
  language: z.string().min(2).max(10).optional(),
  /** Состояние, по которому написан: экран сбрасывает черновик при новом. */
  stateId: z.guid(),
  generatedAt: z.iso.datetime({ offset: true }),
});
export type DealClientMessageDraft = z.infer<typeof dealClientMessageDraftSchema>;

/**
 * Почему черновик не построен — отказ, а не сбой. Отказа по балансу нет:
 * черновик оплачен платформой.
 * - `feature_disabled` — флаг или тариф состояния сделки закрыт;
 * - `feature_unavailable` — флаг или тариф не прочитаны (временный сбой D1);
 * - `not_head` — у сделки уже новое состояние: черновик по устаревшему не пишем;
 * - `crm_processing_inactive` — сделка установки Uspacy, чья обработка
 *   приостановлена или отозвана (или установка снята), как у шага состояния
 *   (#1900), либо установки сделки больше нет или она чужая — вендор не
 *   проверить: её состояние в модель не уходит;
 * - `over_limit_minute` / `over_limit_hour` / `over_limit_day` — потолок
 *   организации, по ступени, которая сработала;
 * - `over_user_limit` — потолок пользователя;
 * - `limit_unavailable` — счётчик потолка недоступен (fail closed).
 */
export const DEAL_CLIENT_MESSAGE_REFUSALS = [
  'feature_disabled',
  'feature_unavailable',
  'not_head',
  'crm_processing_inactive',
  'over_limit_minute',
  'over_limit_hour',
  'over_limit_day',
  'over_user_limit',
  'limit_unavailable',
] as const;
export const dealClientMessageRefusalSchema = z.enum(DEAL_CLIENT_MESSAGE_REFUSALS);
export type DealClientMessageRefusal = z.infer<typeof dealClientMessageRefusalSchema>;

/**
 * workflows-api → edge-api. `costUsd` — себестоимость вызова; edge срезает её
 * клиентской схемой. Сбой (модель не ответила, ответ не разобрался, состояние
 * не прочитано) — 502 `{ code: 'draft_failed', stage }`, а не тело этой схемы.
 */
export const dealClientMessageServiceResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ready'),
    draft: dealClientMessageDraftSchema,
    costUsd: z.number().nonnegative(),
  }),
  z.object({ status: z.literal('refused'), reason: dealClientMessageRefusalSchema }),
]);
export type DealClientMessageServiceResponse = z.infer<
  typeof dealClientMessageServiceResponseSchema
>;

/** Ответ клиенту. Отказы и сбои — HTTP-статус с `code` (`DEAL_CLIENT_MESSAGE_ERROR_CODES`). */
export const dealClientMessageResponseSchema = z.object({
  status: z.literal('ready'),
  draft: dealClientMessageDraftSchema,
});
export type DealClientMessageResponse = z.infer<typeof dealClientMessageResponseSchema>;

/**
 * Коды ошибок маршрута — экран переводит их в слова: 403 `tier_not_eligible`
 * / `feature_disabled`; 409 `not_head` / `crm_processing_inactive`; 429 `over_limit_minute` /
 * `over_limit_hour` / `over_limit_day` / `over_user_limit`; 503
 * `temporarily_unavailable` / `draft_unavailable` / `draft_timeout` /
 * `draft_failed`.
 */
export const DEAL_CLIENT_MESSAGE_ERROR_CODES = [
  'tier_not_eligible',
  'feature_disabled',
  'not_head',
  'crm_processing_inactive',
  'over_limit_minute',
  'over_limit_hour',
  'over_limit_day',
  'over_user_limit',
  'temporarily_unavailable',
  'draft_unavailable',
  'draft_timeout',
  'draft_failed',
] as const;
export type DealClientMessageErrorCode = (typeof DEAL_CLIENT_MESSAGE_ERROR_CODES)[number];
